import { formatDateTime, formatLabel, formatPhoneNumber } from '@/utils/format'
import { normalizeLeadStatuses } from '@/utils/contexts'

export const BUSINESS_QUERY_STATUSES = [
  { value: 'PENDING', title: 'Pending' },
  { value: 'CONTACTED', title: 'Contacted' },
  { value: 'CONVERTED', title: 'Converted' },
  { value: 'REJECTED', title: 'Rejected' },
]

/**
 * Build status picker / filter options from a context's lead_statuses
 * (listContexts). No hardcoded fallback — empty list means the context
 * has not defined statuses yet.
 */
export function buildStatusOptions(statuses) {
  return normalizeLeadStatuses(statuses).map((value) => ({
    value,
    title: businessQueryStatusLabel(value),
  }))
}

function pickString(raw, ...keys) {
  if (!raw || typeof raw !== 'object') return ''
  for (const key of keys) {
    const value = raw[key]
    if (value != null && String(value).trim()) return String(value).trim()
  }
  return ''
}

export function normalizeBusinessQueryStatus(value) {
  return String(value || '')
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_')
}

export function businessQueryStatusLabel(status) {
  const code = normalizeBusinessQueryStatus(status)
  const match = BUSINESS_QUERY_STATUSES.find((item) => item.value === code)
  return match?.title || formatLabel(code) || '—'
}

export function businessQueryStatusTone(status) {
  const code = normalizeBusinessQueryStatus(status)
  if (code === 'CONVERTED' || code === 'WON' || code === 'CLOSED_WON') {
    return 'success'
  }
  if (code === 'CONTACTED' || code === 'IN_PROGRESS' || code === 'OPEN') {
    return 'info'
  }
  if (code === 'PENDING' || code === 'NEW' || code === 'QUEUED') {
    return 'warning'
  }
  if (
    code === 'REJECTED' ||
    code === 'LOST' ||
    code === 'CLOSED_LOST' ||
    code === 'FAILED'
  ) {
    return 'error'
  }
  return 'neutral'
}

export function businessQueryInitials(name) {
  const parts = String(name || '?').trim().split(/\s+/).filter(Boolean)
  if (parts.length >= 2) return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase()
  return (parts[0] || '?').slice(0, 2).toUpperCase()
}

export function businessQueryInquiryText(record) {
  if (!record) return ''
  const raw = record.raw && typeof record.raw === 'object' ? record.raw : record
  const message = pickString(raw, 'message', 'query', 'query_text', 'queryText')
  const comments = pickString(raw, 'comments', 'comment', 'notes')
  return message || comments || String(record.message || '').trim()
}

export function businessQueryInquiryType(record) {
  return businessQueryInquiryText(record) ? 'General inquiry' : 'Callback request'
}

export function businessQueryInquirySummaryBullets(record) {
  const text = businessQueryInquiryText(record)
  if (!text) return []

  return text
    .split(/\r?\n+/)
    .flatMap((line) => line.split(/(?<=[.!?])\s+/))
    .map((part) => part.trim())
    .filter(Boolean)
}

export function normalizeBusinessQueryRecord(item, index = 0) {
  if (!item || typeof item !== 'object') return null

  const requestId = pickString(
    item,
    'request_id',
    'requestId',
    'business_query_id',
    'businessQueryId',
    'query_id',
    'queryId',
    'id',
    'uuid',
  )

  const status = normalizeBusinessQueryStatus(
    pickString(item, 'request_status', 'requestStatus', 'status', 'query_status', 'queryStatus'),
  )

  const name = pickString(
    item,
    'name',
    'full_name',
    'fullName',
    'contact_name',
    'contactName',
    'customer_name',
    'customerName',
    'user_name',
    'userName',
  )

  const email = pickString(
    item,
    'email',
    'email_id',
    'emailId',
    'contact_email',
    'contactEmail',
    'user_email',
    'userEmail',
  )

  const phone = pickString(
    item,
    'phone',
    'phone_number',
    'phoneNumber',
    'mobile',
    'mobile_number',
    'mobileNumber',
    'contact_number',
    'contactNumber',
  )

  const message = pickString(
    item,
    'message',
    'query',
    'query_text',
    'queryText',
    'comments',
    'comment',
    'description',
    'details',
    'notes',
  )

  const company = pickString(
    item,
    'company',
    'company_name',
    'companyName',
    'organisation',
    'organization',
    'org_name',
    'orgName',
  )

  const source = pickString(item, 'source', 'channel', 'origin', 'submitted_via', 'submittedVia')
  const contextName = pickString(item, 'context_name', 'contextName')
  const contextId = pickString(item, 'context_id', 'contextId')
  const typeOfBusiness = pickString(
    item,
    'type_of_business',
    'typeOfBusiness',
    'business_type',
    'businessType',
  )
  const industry = pickString(item, 'industry')
  const comments = pickString(item, 'comments', 'comment', 'notes')

  const createdOn = pickString(
    item,
    'created_on',
    'createdOn',
    'created_at',
    'createdAt',
    'submitted_on',
    'submittedOn',
    'submitted_at',
    'submittedAt',
    'updated_at',
    'updatedAt',
  )

  const disabled =
    item.is_active === false ||
    item.isActive === false ||
    item.disabled === true ||
    item.is_disabled === true

  let customFields = item.custom_fields ?? item.customFields
  if (typeof customFields === 'string') {
    try {
      customFields = JSON.parse(customFields)
    } catch {
      customFields = null
    }
  }
  if (!customFields || typeof customFields !== 'object' || Array.isArray(customFields)) {
    customFields = null
  }

  // AI fields — null until scored after a call / status update with comments.
  const rawScore = item.lead_score ?? item.leadScore
  const leadScore =
    rawScore == null || rawScore === '' || Number.isNaN(Number(rawScore))
      ? null
      : Math.max(0, Math.min(100, Number(rawScore)))
  const leadOverview = pickString(item, 'lead_overview', 'leadOverview')
  const bestTimeRaw = item.best_time_to_call ?? item.bestTimeToCall
  const bestTimeToCall =
    bestTimeRaw == null || bestTimeRaw === '' || Number.isNaN(Number(bestTimeRaw))
      ? null
      : Number(bestTimeRaw)
  const callHistory = Array.isArray(item.call_history ?? item.callHistory)
    ? (item.call_history ?? item.callHistory)
    : null

  // Stamped only by the bulk processor; null for single-form leads.
  const uploadId = pickString(item, 'upload_id', 'uploadId')
  // Read-only, server-computed from the context's unique identifier field.
  const uniqueIdentifierValue = pickString(
    item,
    'unique_identifier_value',
    'uniqueIdentifierValue',
  )
  const callableAgain = item.callable_again ?? item.callableAgain

  // Per-lead override of a RECURRING_REMINDER campaign's default cadence,
  // and the resulting next-due date computed from it — both null for a
  // lead that isn't on a reminder campaign, or is but has no override set.
  const recurrenceIntervalDaysRaw = item.recurrence_interval_days ?? item.recurrenceIntervalDays
  const recurrenceIntervalDays =
    recurrenceIntervalDaysRaw == null || Number.isNaN(Number(recurrenceIntervalDaysRaw))
      ? null
      : Number(recurrenceIntervalDaysRaw)
  // One-off callback request -- set the moment a human or AI agent hears
  // "call me back on X" during any call, distinct from a recurring
  // campaign reminder above. hasCallbackRequested is the plain "does this
  // lead need a callback" flag a list/badge can check without doing its
  // own null/date-math.
  const callbackRequestedOnRaw = item.callback_requested_on ?? item.callbackRequestedOn
  const callbackRequestedOn =
    callbackRequestedOnRaw == null || Number.isNaN(Number(callbackRequestedOnRaw))
      ? null
      : Number(callbackRequestedOnRaw)
  const callbackNote = pickString(item, 'callback_note', 'callbackNote')
  const hasCallbackRequested = callbackRequestedOn != null

  return {
    id: requestId || `lead-${index}`,
    requestId: requestId || '',
    name: name || '—',
    email: email || '—',
    phone: phone ? formatPhoneNumber(phone) : '',
    phoneRaw: phone || '',
    message: message || '',
    comments: comments || '',
    company: company || '',
    contextName: contextName || '',
    contextId: contextId || '',
    typeOfBusiness: typeOfBusiness || '',
    industry: industry || '',
    source: source || '',
    status,
    statusLabel: businessQueryStatusLabel(status),
    statusTone: businessQueryStatusTone(status),
    isActive: !disabled,
    disabled,
    createdOn,
    createdOnLabel: createdOn ? formatDateTime(createdOn) : '—',
    customFields,
    leadScore,
    leadOverview: leadOverview || '',
    bestTimeToCall,
    bestTimeToCallLabel:
      bestTimeToCall != null ? formatDateTime(bestTimeToCall) : '',
    callHistory,
    uploadId: uploadId || '',
    uniqueIdentifierValue: uniqueIdentifierValue || '',
    callableAgain: callableAgain !== false,
    recurrenceIntervalDays,
    callbackRequestedOn,
    callbackRequestedOnLabel: callbackRequestedOn ? formatDateTime(callbackRequestedOn) : '—',
    callbackNote,
    hasCallbackRequested,
    raw: item,
  }
}

/** Tone for the 0-100 AI lead score badge. */
export function leadScoreTone(score) {
  if (score == null) return ''
  if (score >= 70) return 'success'
  if (score >= 40) return 'warning'
  return 'error'
}

export function normalizeBusinessQueriesList(rawList) {
  const list = Array.isArray(rawList) ? rawList : []
  return list
    .map((item, index) => normalizeBusinessQueryRecord(item, index))
    .filter(Boolean)
}
